// Precondition: Shadow DOM available
// Postcondition: Renders horizontal bar chart of portfolio composition
// Invariant: Data sourced from fetchHoldings API response

import { fetchHoldings } from "../../lib/financeapis.js";
import { createLogger } from "../../lib/logger.js";
import type { HoldingRaw } from "../../../shared/api-types.js";

const log = createLogger("ft-portfolio-composition");

const BAR_COLORS = [
  "#c9a84c", // rank 1
  "#8b7a3a", // rank 2
  "#6b5e2d", // rank 3
  "#524821", // rank 4
  "#3d3618", // rank 5
  "#2e2912", // rank 6
];
const OTHER_COLOR = "#1f1f1f";

interface CompositionRow {
  name: string;
  percentage: number;
  isOther: boolean;
  holdingsCount?: number;
}

class PortfolioComposition extends HTMLElement {
  private rows: CompositionRow[] = [];
  private loading = false;
  private error: string | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return ["cik", "q1", "q2", "top"];
  }

  private get topN(): number {
    const attr = this.getAttribute("top");
    return attr ? parseInt(attr, 10) || 6 : 6;
  }

  connectedCallback(): void {
    this.render();
    this.loadData();
  }

  attributeChangedCallback(
    _name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    if (oldValue === newValue || !this.shadowRoot) return;
    this.loadData();
  }

  private async loadData(): Promise<void> {
    const cik = this.getAttribute("cik");
    const q1 = this.getAttribute("q1");
    const q2 = this.getAttribute("q2");
    if (!cik || !q1 || !q2) return;

    this.loading = true;
    this.error = null;
    this.render();

    const result = await fetchHoldings(cik, q1, q2);

    if (!result.ok) {
      log.error("fetchHoldings failed", { cik, q1, q2, error: result.error });
      this.error = "Unable to load holdings data";
      this.loading = false;
      this.render();
      return;
    }

    const holdings = result.data.holdings || [];
    if (holdings.length === 0) {
      this.error = "No holdings data";
      this.loading = false;
      this.render();
      return;
    }

    this.rows = this.computeRows(holdings);
    this.loading = false;
    this.render();
  }

  private computeRows(holdings: HoldingRaw[]): CompositionRow[] {
    const totalValue = holdings.reduce((sum, h) => sum + (h.value || 0), 0);
    if (totalValue === 0) return [];

    const sorted = [...holdings].sort(
      (a, b) => (b.value || 0) - (a.value || 0),
    );
    const topN = this.topN;
    const top = sorted.slice(0, topN);
    const rest = sorted.slice(topN);

    const rows: CompositionRow[] = top.map((h) => ({
      name: h.nameOfIssuer,
      percentage: ((h.value || 0) / totalValue) * 100,
      isOther: false,
    }));

    if (rest.length > 0) {
      const otherValue = rest.reduce((sum, h) => sum + (h.value || 0), 0);
      rows.push({
        name: `Other (${rest.length} holdings)`,
        percentage: (otherValue / totalValue) * 100,
        isOther: true,
        holdingsCount: rest.length,
      });
    }

    return rows;
  }

  private render(): void {
    if (!this.shadowRoot) return;

    const style = document.createElement("style");
    style.textContent = this.getStyles();

    const container = document.createElement("div");
    container.className = "composition";

    if (this.loading) {
      container.innerHTML =
        '<ft-spinner text="Loading composition..."></ft-spinner>';
    } else if (this.error) {
      container.innerHTML = `<div class="error">${this.error}</div>`;
    } else if (this.rows.length > 0) {
      const maxPct = this.rows[0].percentage;
      container.innerHTML = this.rows
        .map((row, i) => this.renderRow(row, i, maxPct))
        .join("");
    }

    this.shadowRoot.innerHTML = "";
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(container);
  }

  private renderRow(
    row: CompositionRow,
    index: number,
    maxPct: number,
  ): string {
    const barWidth = maxPct > 0 ? (row.percentage / maxPct) * 100 : 0;
    const color = row.isOther
      ? OTHER_COLOR
      : BAR_COLORS[index] || BAR_COLORS[BAR_COLORS.length - 1];

    return `
      <div class="row">
        <div class="name">${row.name}</div>
        <div class="bar-track">
          <div class="bar-fill" style="width: ${barWidth.toFixed(1)}%; background: ${color};"></div>
        </div>
        <div class="pct">${row.percentage.toFixed(1)}%</div>
      </div>
    `;
  }

  private getStyles(): string {
    return `
      :host {
        display: block;
        --_font-body: "Space Grotesk", system-ui, sans-serif;
        --_panel: #111111;
        --_border: #161616;
        --_text: #f0f0f0;
        --_text-secondary: #a0a0a0;
        --_text-dim: #525252;
        --_red: #ef4444;
      }

      @media (prefers-color-scheme: light) {
        :host {
          --_panel: #ffffff;
          --_border: #e2e4e8;
          --_text: #1c1c1e;
          --_text-secondary: #666666;
          --_text-dim: #888888;
        }
      }

      * { box-sizing: border-box; margin: 0; padding: 0; }

      .composition {
        display: flex;
        flex-direction: column;
        gap: 0.375em;
      }

      .row {
        display: grid;
        grid-template-columns: 140px 1fr 48px;
        align-items: center;
        gap: 0.5em;
        height: 1.5em;
      }

      .name {
        font: 400 0.625rem/1 var(--_font-body);
        color: var(--_text-secondary);
        text-align: right;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .bar-track {
        height: 0.75em;
        background: var(--_border);
        border-radius: 2px;
        overflow: hidden;
      }

      .bar-fill {
        height: 100%;
        border-radius: 2px;
        transition: width 400ms ease-out;
      }

      .pct {
        font: 500 0.625rem/1 var(--_font-body);
        color: var(--_text);
        font-variant-numeric: tabular-nums;
        text-align: right;
      }

      .error {
        font: 400 0.75rem/1.5 var(--_font-body);
        color: var(--_red);
        text-align: center;
        padding: 1em;
      }

      @media (max-width: 480px) {
        .row {
          grid-template-columns: 100px 1fr 40px;
        }
      }
    `;
  }
}

if (!customElements.get("ft-portfolio-composition")) {
  customElements.define("ft-portfolio-composition", PortfolioComposition);
}
